import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { SPECIALTIES, DOCTOR } from "../data/content";

const NAV_LINKS = [
  { to: "/", label: "Inicio" },
  { to: "/curriculum", label: "Currículum" },
  { to: "/compromisos", label: "Compromiso" },
  { to: "/decalogo", label: "Decálogo" },
  { to: "/blog", label: "Blog" },
  { to: "/testimonios", label: "Testimonios" },
  { to: "/enlaces", label: "Enlaces" },
  { to: "/dato-semana", label: "Dato de la Semana" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [specOpen, setSpecOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setSpecOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md shadow-sm border-b border-slate-200"
          : "bg-white border-b border-slate-100"
      }`}
    >
      {/* Top Bar */}
      <div className="hidden md:block bg-gradient-to-r from-cyan-700 to-teal-700 text-white text-sm">
        <div className="max-w-7xl mx-auto px-4 py-2 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <a href={`tel:${DOCTOR.phone.replace(/\s/g, "")}`} className="flex items-center gap-1.5 hover:text-cyan-100 transition">
              <Phone size={14} />
              <span>{DOCTOR.phone}</span>
            </a>
            <span className="opacity-60">|</span>
            <span className="opacity-90">{DOCTOR.location}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="opacity-90">Lun-Sab · Consulta previa cita</span>
          </div>
        </div>
      </div>

      {/* Main Nav */}
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3 group">
          <div className="w-[116px] h-[99px] rounded-xl bg-gradient-to-br from-cyan-600 to-teal-600 flex items-center justify-center shadow-md group-hover:scale-105 transition-transform overflow-hidden">
            <img src="/images/logo.png" alt="" className="w-full h-full object-contain p-1" />
          </div>
          <div className="leading-tight">
            <div className="font-bold text-slate-900 text-lg tracking-tight">Mental Mente Sano</div>
            <div className="text-xs text-slate-500 hidden sm:block">Dr. César S. Martínez Maldonado</div>
          </div>
        </Link>

        {/* Desktop Menu */}
        <nav className="hidden lg:flex items-center gap-1">
          <NavLink to="/" end className={linkClass}>Inicio</NavLink>

          {/* Especialidades Dropdown */}
          <div className="relative group">
            <button className="px-3 py-2 rounded-lg text-sm font-medium text-slate-700 hover:text-cyan-700 hover:bg-slate-50 transition flex items-center gap-1">
              Especialidades <ChevronDown size={14} />
            </button>
            <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 w-80 bg-white rounded-2xl shadow-xl border border-slate-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 p-2">
              <div className="grid grid-cols-1 gap-0.5">
                {SPECIALTIES.map((s) => (
                  <Link
                    key={s.slug}
                    to={`/especialidades/${s.slug}`}
                    className="flex items-start gap-3 px-3 py-2.5 rounded-lg hover:bg-cyan-50 transition"
                  >
                    <span className="text-xl">{s.emoji}</span>
                    <div>
                      <div className="text-sm font-semibold text-slate-900">{s.title}</div>
                      <div className="text-xs text-slate-500 line-clamp-1">{s.short}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <NavLink to="/curriculum" className={linkClass}>Currículum</NavLink>
          <NavLink to="/compromisos" className={linkClass}>Compromiso</NavLink>
          <NavLink to="/decalogo" className={linkClass}>Decálogo</NavLink>
          <NavLink to="/blog" className={linkClass}>Blog</NavLink>
          <NavLink to="/testimonios" className={linkClass}>Testimonios</NavLink>
          <NavLink
            to="/contacto"
            className="ml-2 px-5 py-2 rounded-full bg-gradient-to-r from-cyan-600 to-teal-600 text-white text-sm font-semibold shadow-md hover:shadow-lg hover:scale-105 transition-all"
          >
            Contacto
          </NavLink>
        </nav>

        {/* Mobile Toggle */}
        <button
          className="lg:hidden p-2 -mr-2 text-slate-700"
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {open && (
        <div className="lg:hidden border-t border-slate-100 bg-white animate-in slide-in-from-top-2 duration-200">
          <nav className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `px-3 py-2.5 rounded-lg text-base font-medium transition ${
                    isActive ? "bg-cyan-50 text-cyan-700" : "text-slate-700 hover:bg-slate-50"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}

            <button
              onClick={() => setSpecOpen((v) => !v)}
              className="px-3 py-2.5 rounded-lg text-base font-medium text-slate-700 hover:bg-slate-50 flex items-center justify-between"
            >
              <span>Especialidades</span>
              <ChevronDown size={16} className={`transition-transform ${specOpen ? "rotate-180" : ""}`} />
            </button>
            {specOpen && (
              <div className="pl-3 flex flex-col gap-0.5 mb-2">
                {SPECIALTIES.map((s) => (
                  <Link
                    key={s.slug}
                    to={`/especialidades/${s.slug}`}
                    className="px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-cyan-50 hover:text-cyan-700 flex items-center gap-2"
                  >
                    <span>{s.emoji}</span> {s.title}
                  </Link>
                ))}
              </div>
            )}

            <Link
              to="/contacto"
              className="mt-2 px-4 py-3 rounded-full bg-gradient-to-r from-cyan-600 to-teal-600 text-white font-semibold text-center shadow-md"
            >
              Agendar consulta
            </Link>
            <a
              href={`tel:${DOCTOR.phone.replace(/\s/g, "")}`}
              className="px-4 py-3 rounded-full border-2 border-slate-200 text-slate-700 font-semibold text-center flex items-center justify-center gap-2"
            >
              <Phone size={16} /> {DOCTOR.phone}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}

function linkClass({ isActive }) {
  return `px-3 py-2 rounded-lg text-sm font-medium transition ${
    isActive ? "text-cyan-700 bg-cyan-50" : "text-slate-700 hover:text-cyan-700 hover:bg-slate-50"
  }`;
}
